import React from 'react';
import { AlertTriangle, Plus, ArrowUp } from 'lucide-react';

interface IngredienteSimilarReceta {
  id: number;
  nombre: string;
  categoria: string;
  cantidad_actual: string;
  unidad_actual: string;
  ingrediente_receta_id: number;
}

interface IngredienteNuevo {
  id: number;
  nombre: string;
  categoria: string;
}

interface RecetaErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  ingredientesSimilares?: IngredienteSimilarReceta[];
  ingredienteNuevo?: IngredienteNuevo;
  onIncrementarCantidad?: (
    ingredienteRecetaId: number,
    cantidadAdicional: number
  ) => void;
  onAgregarDeTodasFormas?: () => void;
}

const RecetaErrorModal: React.FC<RecetaErrorModalProps> = ({
  isOpen,
  onClose,
  message,
  ingredientesSimilares = [],
  ingredienteNuevo,
  onIncrementarCantidad,
  onAgregarDeTodasFormas,
}) => {
  if (!isOpen) return null;

  const [cantidadIncremento, setCantidadIncremento] = React.useState<{
    [key: number]: number;
  }>({});

  const handleIncrementarCantidad = (ingredienteRecetaId: number) => {
    const cantidad = cantidadIncremento[ingredienteRecetaId] || 1;
    onIncrementarCantidad?.(ingredienteRecetaId, cantidad);
    onClose();
  };

  const handleAgregarNuevo = () => {
    onAgregarDeTodasFormas?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Ingredientes Similares en la Receta
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
            {message}
          </p>

          {ingredienteNuevo && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
                Ingrediente que intentas agregar:
              </h4>
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p>
                  <strong>{ingredienteNuevo.nombre}</strong>
                </p>
                <p>Categoría: {ingredienteNuevo.categoria}</p>
              </div>
            </div>
          )}

          {ingredientesSimilares.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Ingredientes similares ya en la receta:
              </p>

              <div className="space-y-3">
                {ingredientesSimilares.map((ingrediente) => (
                  <div
                    key={ingrediente.ingrediente_receta_id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {ingrediente.nombre}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Categoría: {ingrediente.categoria}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Cantidad actual: {ingrediente.cantidad_actual}{' '}
                          {ingrediente.unidad_actual}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            +
                          </span>
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            defaultValue="1"
                            className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                            onChange={(e) =>
                              setCantidadIncremento((prev) => ({
                                ...prev,
                                [ingrediente.ingrediente_receta_id]:
                                  parseFloat(e.target.value) || 1,
                              }))
                            }
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {ingrediente.unidad_actual}
                          </span>
                        </div>

                        <button
                          onClick={() =>
                            handleIncrementarCantidad(
                              ingrediente.ingrediente_receta_id
                            )
                          }
                          className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors duration-200"
                        >
                          <ArrowUp className="h-4 w-4 mr-1" />
                          Aumentar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>Recomendación:</strong> Te sugerimos aumentar la
                  cantidad del ingrediente existente para mantener la receta
                  organizada y evitar duplicados.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
          >
            Cancelar
          </button>

          <button
            onClick={handleAgregarNuevo}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar como Nuevo Ingrediente
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecetaErrorModal;
